import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useClient } from 'urql';

import {
  COMPOSITION_GET_DATA_V2,
  type CompositionV2Filters,
  PositionsEntityKind,
} from '@revelio/data-access';

import type { Competitor } from './useCompetitors';
import type { ResolvedProfileIds } from './useResolveProfileIds';

export type TrendDirection = 'growing' | 'stable' | 'declining';

export interface CompanyMetrics {
  companyId: number;
  shortName: string;
  longName: string;
  headcount: number | null;
  salary: number | null;
  tenure: number | null;
  growthRate: number | null;
  hiringRate: number | null;
  attritionRate: number | null;
  growthTrend: TrendDirection;
  hiringTrend: TrendDirection;
  attritionTrend: TrendDirection;
  /** Raw time-series for salary to compute growth projections */
  salaryTimeseries: Array<{ id: number; value: number | null }>;
}

interface UseCompetitorCompositionResult {
  metrics: CompanyMetrics[];
  isLoading: boolean;
  error: string | null;
}

/** Maximum entities per API request */
const BATCH_SIZE = 10;

/** Split an array into chunks of a given size */
const chunk = <T>(arr: T[], size: number): T[][] => {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};

const getLatestValue = (
  timeseries:
    | ReadonlyArray<{ id: number; value?: number | null } | null>
    | null
    | undefined
): number | null => {
  if (!timeseries || timeseries.length === 0) return null;
  const last = timeseries[timeseries.length - 1];
  return last?.value ?? null;
};

const computeTrend = (
  timeseries:
    | ReadonlyArray<{ id: number; value?: number | null } | null>
    | null
    | undefined
): TrendDirection => {
  if (!timeseries || timeseries.length < 6) return 'stable';

  // Compare average of last 3 months vs. 3 months before that
  const recent = timeseries.slice(-3);
  const prior = timeseries.slice(-6, -3);

  const avgRecent =
    recent.reduce((sum, p) => sum + (p?.value ?? 0), 0) / recent.length;
  const avgPrior =
    prior.reduce((sum, p) => sum + (p?.value ?? 0), 0) / prior.length;

  if (avgPrior === 0) return 'stable';
  const change = (avgRecent - avgPrior) / Math.abs(avgPrior);

  if (change > 0.05) return 'growing';
  if (change < -0.05) return 'declining';
  return 'stable';
};

/**
 * Parse a single composition entity response into CompanyMetrics.
 */
type NullableTimeseries =
  | ReadonlyArray<{ id: number; value?: number | null } | null>
  | null
  | undefined;

export const parseEntity = (entity: {
  metadata?: {
    id?: number | null;
    short_name?: string | null;
    long_name?: string | null;
  } | null;
  metrics?: {
    headcount?: NullableTimeseries;
    salary?: NullableTimeseries;
    tenure?: NullableTimeseries;
    growth_rate?: NullableTimeseries;
    hiring_rate?: NullableTimeseries;
    attrition_rate?: NullableTimeseries;
  } | null;
}): CompanyMetrics => {
  const m = entity.metrics;
  const id = entity.metadata?.id ?? 0;

  const salaryTs = (m?.salary ?? [])
    .filter((p): p is NonNullable<typeof p> => p !== null && p !== undefined)
    .map((p) => ({ id: p.id, value: p.value ?? null }));

  return {
    companyId: id,
    shortName: entity.metadata?.short_name ?? '',
    longName: entity.metadata?.long_name ?? '',
    headcount: getLatestValue(m?.headcount),
    salary: getLatestValue(m?.salary),
    tenure: getLatestValue(m?.tenure),
    growthRate: getLatestValue(m?.growth_rate),
    hiringRate: getLatestValue(m?.hiring_rate),
    attritionRate: getLatestValue(m?.attrition_rate),
    growthTrend: computeTrend(m?.growth_rate),
    hiringTrend: computeTrend(m?.hiring_rate),
    attritionTrend: computeTrend(m?.attrition_rate),
    salaryTimeseries: salaryTs,
  };
};

export const useCompetitorComposition = (
  competitors: Competitor[],
  resolvedIds: ResolvedProfileIds,
  additionalCompanyIds?: number[]
): UseCompetitorCompositionResult => {
  const gqlClient = useClient();
  const [metrics, setMetrics] = useState<CompanyMetrics[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allCompanyIds = useMemo(() => {
    const ids = competitors.map((c) => c.id);
    if (resolvedIds.companyRcid && !ids.includes(resolvedIds.companyRcid)) {
      ids.unshift(resolvedIds.companyRcid);
    }
    for (const id of additionalCompanyIds ?? []) {
      if (!ids.includes(id)) {
        ids.push(id);
      }
    }
    return ids;
  }, [competitors, resolvedIds.companyRcid, additionalCompanyIds]);

  // Stable key for the effect dependency
  const idsKey = allCompanyIds.join(',');

  // Track whether the component is still mounted
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchBatched = useCallback(async () => {
    if (allCompanyIds.length === 0) {
      setMetrics([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const batches = chunk(allCompanyIds, BATCH_SIZE);
      const filters: CompositionV2Filters = { raw: false };

      const results = await Promise.all(
        batches.map((batch) => {
          const entities = batch.map((id) => ({
            EntID: id,
            EntType: PositionsEntityKind.Company,
          }));
          return gqlClient
            .query(COMPOSITION_GET_DATA_V2, { entities, filters })
            .toPromise();
        })
      );

      if (!mountedRef.current) return;

      const merged: CompanyMetrics[] = [];
      for (const result of results) {
        if (result.error) {
          setError('Failed to load competitor metrics');
          continue;
        }
        const rawEntities = result.data?.composition_v2?.entities;
        if (!rawEntities) continue;

        for (const entity of rawEntities) {
          if (entity) {
            merged.push(parseEntity(entity));
          }
        }
      }

      setMetrics(merged);
    } catch {
      if (mountedRef.current) {
        setError('Failed to load competitor metrics');
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [allCompanyIds, gqlClient]); // eslint-disable-line react-hooks/exhaustive-deps

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    fetchBatched();
  }, [idsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return { metrics, isLoading, error };
};
